import { Link } from "react-router-dom";

const ROLE_COLORS = {
	"Solution Architect": "bg-purple-100 text-purple-800",
	"Senior Technical Consultant": "bg-blue-100 text-blue-800",
	"Technical Consultant": "bg-green-100 text-green-800",
	"Graphics & Web Designer": "bg-pink-100 text-pink-800",
};

export default function ResourceCard({
	resource,
	selectable = false,
	selected = false,
	onToggleSelect,
	onRequestSelect,
	showContactDetails = false,
}) {
	const {
		id,
		role,
		experience,
		skills,
		categories,
		name,
		email,
		contact_number,
	} = resource;
	const competencyRank = {
		Expert: 3,
		Intermediate: 2,
		Beginner: 1,
	};

	const topSkills = Object.entries(skills || {})
		.filter(([, competency]) =>
			["Expert", "Intermediate", "Beginner"].includes(competency),
		)
		.sort(([, a], [, b]) => (competencyRank[b] || 0) - (competencyRank[a] || 0))
		.slice(0, 5);

	const roleColor = ROLE_COLORS[role] || "bg-gray-100 text-gray-800";
	const expNum =
		Number.parseInt(String(experience).replaceAll(/\D/g, ""), 10) || 0;

	return (
		<div className="card p-5 flex flex-col gap-4 group hover:border-primary-200 hover:shadow-lg transition-all duration-200">
			<div className="flex items-start justify-between gap-3">
				<div>
					<div className="flex items-center gap-2 mb-1">
						<div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
							{String(id).padStart(3, "0")}
						</div>
						<div>
							<div className="text-xs text-gray-400 font-medium">
								Resource ID-{id}
							</div>
							<div className="font-semibold text-gray-900 text-sm leading-tight">
								{role}
							</div>
						</div>
					</div>
				</div>
				<span className={`badge ${roleColor} flex-shrink-0`}>
					{expNum}+ yrs
				</span>
			</div>

			<div className="flex flex-wrap gap-1.5">
				{(categories || []).slice(0, 3).map((cat) => {
					// Handle both string format (legacy) and object format (new)
					const categoryName = typeof cat === "string" ? cat : cat?.name;
					const competency = typeof cat === "string" ? null : cat?.competency;
					return (
						<div
							key={typeof cat === "string" ? cat : cat?.id}
							className="flex items-center gap-1.5"
						>
							<span className="badge bg-primary-50 text-primary-700 border border-primary-100 text-xs">
								{categoryName}
							</span>
							{competency && (
								<span
									className={`text-xs font-medium ${
										competency === "Expert"
											? "text-green-600"
											: competency === "Intermediate"
												? "text-yellow-600"
												: "text-orange-600"
									}`}
								>
									{competency}
								</span>
							)}
						</div>
					);
				})}
			</div>
			<div className="flex-1">
				<div className="text-xs text-gray-500 font-medium mb-2">TOP SKILLS</div>
				<div className="flex flex-wrap gap-1.5">
					{topSkills.map(([skill, level]) => (
						<span
							key={skill}
							title={level}
							className={`badge text-xs ${
								level === "Expert"
									? "bg-green-50 text-green-700 border-green-200"
									: level === "Intermediate"
										? "bg-yellow-50 text-yellow-700 border-yellow-200"
										: "bg-orange-50 text-orange-700 border border-orange-200"
							}`}
						>
							{skill}
						</span>
					))}
					{topSkills.length === 0 && (
						<span className="text-xs text-gray-400">No skills data</span>
					)}
				</div>
			</div>

			{showContactDetails && (
				<div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs sm:text-sm space-y-1.5">
					<div className="text-gray-700">
						<span className="font-semibold">Name:</span>{" "}
						<span className="text-gray-900">{name || "-"}</span>
					</div>
					<div className="text-gray-700">
						<span className="font-semibold">Email:</span>{" "}
						<span className="text-gray-900 break-all">{email || "-"}</span>
					</div>
					<div className="text-gray-700">
						<span className="font-semibold">Contact:</span>{" "}
						<span className="text-gray-900">{contact_number || "-"}</span>
					</div>
				</div>
			)}

			<div className="flex items-center gap-2 pt-2 border-t border-gray-100">
				<Link
					to={`/resources/${id}`}
					className="flex-1 text-center text-sm font-medium text-primary-600 hover:text-primary-800 py-1.5 rounded-md hover:bg-primary-50 transition-colors"
				>
					View Profile
				</Link>
				{selectable && (
					<button
						type="button"
						onClick={() => {
							if (selected) {
								onToggleSelect && onToggleSelect(id);
								return;
							}
							if (onRequestSelect) {
								onRequestSelect(id, resource);
								return;
							}
							onToggleSelect && onToggleSelect(id);
						}}
						className={`text-sm font-medium py-1.5 px-3 rounded-md transition-colors border ${
							selected
								? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
								: "text-primary-700 border-primary-200 hover:bg-primary-50"
						}`}
						aria-label={selected ? "Remove from inquiry" : "Add to inquiry"}
					>
						{selected ? "✓" : "+"}
					</button>
				)}
			</div>
		</div>
	);
}
